//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import '@legacy-components/common/universal-image-actions/universal-image-actions-one-page-buttons';
import getAreNoCheckboxesSelected from '@uia/approve-for-galleries/getAreNoCheckboxesSelected';
import { FeatureNames } from '@bvengineering/curalate-features';
import universalImageActionsWorkflowTemplate from '@legacy-components/common/universal-image-actions/universal-image-actions-workflow.html';

import _ from "js/underscore-mixins";

(function () {
  const uia = angular.module('uia');

  /*
   * Factories
   */

  uia.factory('BaseActionsWorkflow', () => {
    const BaseActionsWorkflow = function () {
      this.states = [];
      this.index = 0;
      this.name = '';
    };

    BaseActionsWorkflow.prototype.getStates = function () {
      return this.states;
    };

    BaseActionsWorkflow.prototype.getStateIndex = function () {
      return this.index;
    };

    BaseActionsWorkflow.prototype.setStateIndex = function (index) {
      this.index = index;
    };

    BaseActionsWorkflow.prototype.getCurrentState = function () {
      return this.states[this.index];
    };

    BaseActionsWorkflow.prototype.getNextState = function () {
      let returnValue;
      if (this.getCurrentState() !== _.last(this.states)) {
        returnValue = this.states[this.index + 1];
      }
      return returnValue;
    };

    BaseActionsWorkflow.prototype.getNextState = function () {
      if (this.index === this.states.length - 1) {
        return null;
      }

      return this.states[this.index + 1];
    };

    BaseActionsWorkflow.prototype.getPreviousState = function () {
      if (this.index === 0) {
        return null;
      }

      return this.states[this.index - 1];
    };

    BaseActionsWorkflow.prototype.goToNext = function () {
      if (this.index === this.states.length - 1) {
        return null;
      }

      return this.states[++this.index];
    };

    BaseActionsWorkflow.prototype.goToPrevious = function () {
      if (this.index === 0) {
        return null;
      }

      return this.states[--this.index];
    };

    BaseActionsWorkflow.prototype.appendState = function (state) {
      const existingState = _(this.states).find((name) => {
        return name === state.name;
      });
      if (!existingState) {
        this.states.push(state.name);
      }
    };

    BaseActionsWorkflow.prototype.removeState = function (state) {
      this.states = _(this.states).reject((name) => {
        return name === state.name;
      });
    };

    BaseActionsWorkflow.prototype.goToState = function (state) {
      const idx = this.states.indexOf(state);
      let returnValue;
      if (idx !== -1) {
        this.index = idx;
        returnValue = this.states[this.index];
      }
      return returnValue;
    };

    return BaseActionsWorkflow;
  });

  uia.factory('ApproveForFanreelWorkflow', [
    'BaseActionsWorkflow',
    'SessionVarsService',
    'UniversalImageActionsView',
    function (
      BaseActionsWorkflow,
      SessionVarsService,
      UniversalImageActionsView,
    ) {
      const ApproveForFanreelWorkflow = function () {
        this.states = [
          UniversalImageActionsView.ApproveForFanreel.name,
          UniversalImageActionsView.Productize.name,
          UniversalImageActionsView.Labels.name,
          UniversalImageActionsView.RightsRequest.name,
        ];

        this.index = 0;
        this.name = 'ApproveForFanreel';
      };

      ApproveForFanreelWorkflow.prototype = Object.create(
        BaseActionsWorkflow.prototype,
      );

      return ApproveForFanreelWorkflow;
    },
  ]);

  uia.factory('ApproveForGalleriesWorkflow', [
    '$ngRedux',
    'BaseActionsWorkflow',
    'UniversalImageActionsView',
    'UniversalImageActionsWorkflowActions',
    'SessionVarsService',
    function (
      $ngRedux,
      BaseActionsWorkflow,
      UniversalImageActionsView,
      UniversalImageActionsWorkflowActions,
      SessionVarsService,
    ) {
      let self;

      const isRightsManagementEnabled = SessionVarsService.isFeatureEnabled(
        FeatureNames.RightsManagementPage,
      );
      const ApproveForGalleriesWorkflow = function () {
        const isBrandMentioned =
          $ngRedux.getState().uiaModal.rightsRequest.canGraphRequestRights;

        const isAutomated =
          $ngRedux.getState().accountSettings.rightsManagement
            .automateRightsRequests.automationConfigurations.isAutomated;

        const canAutoRightsRequest = isBrandMentioned && isAutomated;

        this.states = [
          UniversalImageActionsView.ApproveForGalleries.name,
          UniversalImageActionsView.Productize.name,
          UniversalImageActionsView.Labels.name,
          ...(isRightsManagementEnabled && !canAutoRightsRequest
            ? [UniversalImageActionsView.RightsRequest.name]
            : []), // Used for BV Connect clients without rights management
        ];

        this.index = 0;
        this.name = 'ApproveForGalleries';
        self = this;
      };

      ApproveForGalleriesWorkflow.prototype = Object.create(
        BaseActionsWorkflow.prototype,
      );
      const dispatchWrapper = function (fx) {
        return function (...args) {
          const res = fx.apply(self, args);
          $ngRedux.dispatch(
            UniversalImageActionsWorkflowActions.setCurrentIndex(self.index),
          );
          return res;
        };
      };

      ApproveForGalleriesWorkflow.prototype.setStateIndex = dispatchWrapper(
        ApproveForGalleriesWorkflow.prototype.setStateIndex,
      );
      ApproveForGalleriesWorkflow.prototype.goToNext = dispatchWrapper(
        ApproveForGalleriesWorkflow.prototype.goToNext,
      );
      ApproveForGalleriesWorkflow.prototype.goToPrevious = dispatchWrapper(
        ApproveForGalleriesWorkflow.prototype.goToPrevious,
      );
      ApproveForGalleriesWorkflow.prototype.goToState = dispatchWrapper(
        ApproveForGalleriesWorkflow.prototype.goToState,
      );

      return ApproveForGalleriesWorkflow;
    },
  ]);

  uia.factory('ScheduleNowWorkflow', [
    'BaseActionsWorkflow',
    'UniversalImageActionsView',
    function (BaseActionsWorkflow, UniversalImageActionsView) {
      const ScheduleNowWorkflow = function () {
        this.states = [UniversalImageActionsView.ScheduleNow.name];
        this.index = 0;
        this.name = 'ScheduleNow';
      };

      ScheduleNowWorkflow.prototype = Object.create(
        BaseActionsWorkflow.prototype,
      );

      return ScheduleNowWorkflow;
    },
  ]);

  uia.factory('CreateRevealWorkflow', [
    'BaseActionsWorkflow',
    'UniversalImageActionsService',
    'UniversalImageActionsView',
    'SessionVarsService',
    function (
      BaseActionsWorkflow,
      UniversalImageActionsService,
      UniversalImageActionsView,
      SessionVarsService,
    ) {
      // Determines the reveal state (reveal, reveal2, revolve)
      const determineRevealState = function () {
        const reveal2Enabled = SessionVarsService.isFeatureEnabled('reveal_v2');

        if (reveal2Enabled) {
          return UniversalImageActionsView.Reveal2.name;
        }

        return UniversalImageActionsView.Reveal.name;
      };

      const revealState = determineRevealState();
      const CreateRevealWorkflow = function () {
        this.states = [UniversalImageActionsView.Productize.name, revealState];

        this.index = 0;
        this.name = 'CreateReveal';
      };

      CreateRevealWorkflow.prototype = Object.create(
        BaseActionsWorkflow.prototype,
      );

      return CreateRevealWorkflow;
    },
  ]);

  uia.factory('CreateOnePageWorkflow', [
    'BaseActionsWorkflow',
    'UniversalImageActionsView',
    function (BaseActionsWorkflow, UniversalImageActionsView) {
      const CreateOnePageWorkflow = function () {
        this.states = [
          UniversalImageActionsView.Productize.name,
          UniversalImageActionsView.OnePage.name,
        ];

        this.index = 0;
        this.name = 'CreateOnePage';
      };

      CreateOnePageWorkflow.prototype = Object.create(
        BaseActionsWorkflow.prototype,
      );

      return CreateOnePageWorkflow;
    },
  ]);

  uia.factory('CreateAdaptiveLinkWorkflow', [
    'BaseActionsWorkflow',
    'SessionVarsService',
    'UniversalImageActionsService',
    'UniversalImageActionsView',
    function (
      BaseActionsWorkflow,
      SessionVarsService,
      UniversalImageActionsService,
      UniversalImageActionsView,
    ) {
      const isShowroomLivePreviewEnabled = SessionVarsService.isFeatureEnabled(
        'showroom_self_serve_v2',
      );

      const CreateAdaptiveLinkWorkflow = function () {
        if (isShowroomLivePreviewEnabled) {
          this.states = [
            UniversalImageActionsView.Productize.name,
            UniversalImageActionsView.TributaryCustomizeProducts.name,
            UniversalImageActionsView.TributaryCustomizeMedia.name,
            UniversalImageActionsView.TributaryAddOns.name,
            UniversalImageActionsView.TributaryCustomizeLinks.name,
          ];
        } else {
          this.states = [
            UniversalImageActionsView.Productize.name,
            UniversalImageActionsView.Tributary.name,
          ];
        }

        this.index = 0;
        this.name = 'CreateAdaptiveLink';
      };

      CreateAdaptiveLinkWorkflow.prototype = Object.create(
        BaseActionsWorkflow.prototype,
      );

      return CreateAdaptiveLinkWorkflow;
    },
  ]);

  /*
   * Services
   */

  /**
   * UniversalImageActionsWorkflowService
   *
   * Exposes a function which takes an workflow type and returns an instance of {workflowType}Workflow
   * Uses an $injector to future-proof implementation by enabling the service to not declare every implementation of BaseActionsWorkflow
   * as a dependency and rather inject the factory at method call time.
   */
  uia.service('UniversalImageActionsWorkflowService', [
    '$injector',
    function ($injector) {
      this.workflowForName = function (workflowName) {
        return $injector.instantiate($injector.get(`${workflowName}Workflow`));
      };
    },
  ]);

  /*
   * Directives
   */

  uia.directive('universalImageActionsWorkflow', () => {
    return {
      restrict: 'E',
      scope: {},
      require: ['^universalImageActionsModal'],
      template: universalImageActionsWorkflowTemplate,
      controller: [
        '$scope',
        '$rootScope',
        '$state',
        '$window',
        '$ngRedux',
        'UniversalImageActionsService',
        'UniversalImageActionsView',
        'PermissionsService',
        'UniversalImageActionsRightsOptionsService',
        function (
          $scope,
          $rootScope,
          $state,
          $window,
          $ngRedux,
          UniversalImageActionsService,
          UniversalImageActionsView,
          PermissionsService,
          UniversalImageActionsRightsOptionsService,
        ) {
          $scope.model = {
            workflow: UniversalImageActionsService.getCurrentWorkflow(),
            nextState: null,
            previousState: null,
            outstandingAsyncAction: null,
            showProductsRequiredMsg: false,
            nextDisabled: false,
            numTaggedProducts: 0,
          };

          $rootScope.$on('uia:num-tagged-products-changed', () => {
            const requiresProductsPanels = [
              UniversalImageActionsView.Tributary.name,
              UniversalImageActionsView.TributaryCustomizeProducts.name,
              UniversalImageActionsView.TributaryCustomizeMedia.name,
              UniversalImageActionsView.TributaryCustomizeLinks.name,
              UniversalImageActionsView.OnePage.name,
            ];
            const nextStateRequiresProducts = _.contains(
              requiresProductsPanels,
              $scope.model.workflow.getNextState(),
            );
            $scope.model.numTaggedProducts =
              UniversalImageActionsService.getNumTaggedProducts();
            $scope.model.nextDisabled =
              nextStateRequiresProducts && $scope.model.numTaggedProducts === 0;
            $scope.model.showProductsRequiredMsg =
              nextStateRequiresProducts && $scope.model.numTaggedProducts === 0;
          });

          // This is an event from the UIARightsOptions React Component (via UIARightsFlow)
          // it is needed so we can control how the "Previous" buttons behave when
          // UIARightsOptions is rendered within another flow, like Galleries Rights Request
          $rootScope.$on('uia:rights:sub-option-selected', () => {
            UniversalImageActionsRightsOptionsService.setShouldShowRightsOptions(
              false,
            );
          });

          $scope.model.isNoGallerySelected = function () {
            const isProductizeNext =
              $scope.model.workflow.getNextState() ===
              UniversalImageActionsView.Productize.name;

            const areNoCheckboxesSelected = getAreNoCheckboxesSelected(
              $ngRedux.getState(),
            );

            return isProductizeNext && areNoCheckboxesSelected;
          };

          function updateStatePreviews() {
            $scope.model.nextState = $scope.model.workflow.getNextState();
            $scope.model.previousState =
              $scope.model.workflow.getPreviousState();
          }

          $scope.model.next = function () {
            if (
              $scope.model.nextDisabled ||
              $scope.model.isNoGallerySelected()
            ) {
              return;
            }

            let nextState = $scope.model.workflow.goToNext();

            /* If next step is to create the Reveal code, user permission needs to checked before they proceed on creating reveal. 
               If user don't have required permission, don't allow user to proceeding for next step as per CUL-35 */
            if (
              (nextState == 'Reveal2' ||
                nextState == 'Reveal' ||
                nextState == 'Revolve') &&
              !PermissionsService.hasPermissionForTask(
                'universal_image_actions:generate_reveal',
              )
            ) {
              nextState = $scope.model.workflow.goToPrevious();
            }

            if (nextState) {
              $scope.model.animateForward();

              // Navigate to the next workflow state, passing the new state and telling ui-router
              // to reload the parent state so the new overlay template will load
              $state.go(`^.${nextState}`);
            }

            updateStatePreviews();
          };

          $scope.model.previous = function () {
            // If we're in a rights sub-option view (when rights options are hidden), go back to rights options
            if (
              !UniversalImageActionsRightsOptionsService.getShouldShowRightsOptions() &&
              $scope.model.workflow.getCurrentState() ===
                UniversalImageActionsView.RightsRequest.name
            ) {
              UniversalImageActionsRightsOptionsService.setShouldShowRightsOptions(
                true,
              );

              $scope.model.animateBackward();
              return;
            }

            const previousState = $scope.model.workflow.goToPrevious();
            $scope.model.animateBackward();

            if (previousState) {
              // Navigate to the next workflow state, passing the new state and telling ui-router
              // to reload the parent state so the new overlay template will load
              $state.go(`^.${previousState}`);
            } else {
              UniversalImageActionsService.setCurrentWorkflow(null);
              $state.go('Home');
            }

            updateStatePreviews();
          };

          $scope.$on(
            'uia:modal:change-workflow-state',
            ($event, requestedState, requestedIndex) => {
              $scope.model.workflow.setStateIndex(requestedIndex);
              $state.go(`^.${requestedState}`);
              updateStatePreviews();
            },
          );

          $scope.$on('uia:workflow:loading', ($event, state) => {
            $scope.model.outstandingAsyncAction = state;
            updateStatePreviews();
          });

          this.disableNext = function () {
            $scope.model.nextDisabled = true;
          };

          this.enableNext = function () {
            $scope.model.nextDisabled = false;
          };

          //
          // Initialization
          //
          updateStatePreviews();
        },
      ],
      link(scope, element, attrs, controllers) {
        const universalImageActionsModalController = controllers[0];

        scope.model.animateForward = function () {
          universalImageActionsModalController.animateForward();
        };

        scope.model.animateBackward = function () {
          universalImageActionsModalController.animateBackward();
        };
      },
    };
  });

  uia.filter('stateName', [
    'UniversalImageActionsView',
    function (UniversalImageActionsView) {
      return function (state) {
        return UniversalImageActionsView[state]
          ? UniversalImageActionsView[state].label
          : '';
      };
    },
  ]);
})();
